import { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { format, parseISO } from 'date-fns';
import { FIXTURE_MATCH_TIME } from '../../constants';
import { Club, GoalType, Match, MatchStatus, LiveFeedPhase, PlayerLineup } from '@repo/types';
import type { GoalTypeInterface } from '@repo/types';
import { ClubNameType } from '../../contracts';
import { isFutureMatch, parseMatchStatus } from '../../../utils/parseMatchStatus';
import { formatMatchMinute, formatPhaseAwareMinute, matchHadExtraTime } from '../../../utils/formatMatchMinute';
import { ImageVariant, Image } from '../../../common/components/Image';
import { Link } from 'react-router';
import { isMobileClient } from '@ui/utils/isMobileClient';
import { LinkSize, LinkVariant } from '@ui/common/components/Link';
import { Link as LinkButton } from '@ui/common/components/Link';
import { defaultTeamBadge, chevronRightIcon } from '@ui/common/staticAssets.constants';
import { resolveTimelineGlyph } from '../MatchDetail/lineupGlyphs';
import type { TimelineEventType } from '../MatchDetail/buildTimeline';
import { PinIcon, WhistleIcon, PeopleIcon } from './fixtureIcons';

/**
 * Where a desktop row shows attendance. `footer` is the meta line under the row (inside the
 * expanded detail when the row expands); `column` is an always-visible cell at the row's right
 * edge. Mobile always uses `footer`.
 */
export type AttendancePlacement = 'footer' | 'column';

export type FixtureProps = {
  fixture: Match;
  showAttendance?: boolean;
  attendancePlacement?: AttendancePlacement;
  className?: string;
  nameType?: ClubNameType;
  style?: React.CSSProperties;
  isFixtureExpandable?: boolean;
  isExpanded?: boolean;
};

type GoalInfo = {
  minute: number;
  phase?: LiveFeedPhase;
};

/**
 * One player's occurrences of a single event type on one side — e.g. "two normal goals" or
 * "one penalty". Grouped by (player, type) rather than by player alone so a player who scored
 * a normal goal and a penalty gets a line per glyph instead of one glyph standing for both.
 */
type PlayerEventGroup = {
  player: string;
  // Master football_players id, present only once the scorer is reconciled —
  // absent means no profile page exists, so the name renders as plain text.
  profileId?: string;
  type: QuickViewEventType;
  occurrences: GoalInfo[];
};

/**
 * The event types this compact view shows (SC-718). Substitutions are deliberately excluded —
 * they belong on the match detail Timeline, not in a scorer summary beside the score.
 */
type QuickViewEventType = Exclude<TimelineEventType, 'sub'>;

const QUICK_VIEW_EVENT_ORDER: QuickViewEventType[] = ['goal', 'penalty', 'own-goal', 'yellow', 'second-yellow', 'red'];

// ── Class name constants ───────────────────────────────────────

const TEAM_LOGO_CLASS = 'fixture-match__team-logo';
const SHOW_MORE_BTN_CLASS = 'fixture-match__show-more-btn';
const ATTENDANCE_CLASS = 'fixture-match__attendance';
const TEAM_LINK_CLASS = 'fixture-match__team-link';

// Both half-time breaks — regular and the one inside extra time. Neither has a
// minute to tick, so they take the live pill's slot rather than adding a second
// badge under the score: a pulsing LIVE dot above an HT badge below reads as two
// conflicting states for one match.
const HALF_TIME_STATUSES: MatchStatus[] = [MatchStatus.HalfTime, MatchStatus.HalfTimeInExtraTime];

const isHalfTimeBreak = (status: MatchStatus): boolean => HALF_TIME_STATUSES.includes(status);

// ── Helpers ───────────────────────────────────────────────────

// Goals first (in QUICK_VIEW_EVENT_ORDER), then within a type the busiest scorer, then earliest.
// Keeps the pre-SC-718 ordering for a list of plain goals, which is all most fixtures have.
const sortEventGroups = (a: PlayerEventGroup, b: PlayerEventGroup) => {
  const byType = QUICK_VIEW_EVENT_ORDER.indexOf(a.type) - QUICK_VIEW_EVENT_ORDER.indexOf(b.type);
  if (byType !== 0) {
    return byType;
  }

  if (a.occurrences.length !== b.occurrences.length) {
    return a.occurrences.length > b.occurrences.length ? -1 : 1;
  }

  // No optional chaining: a group is only ever created from an occurrence, so [0] always exists —
  // and this package's tsconfig does not set noUncheckedIndexedAccess, so a guard here would be a
  // branch no test could reach.
  return a.occurrences[0].minute - b.occurrences[0].minute;
};

function getClubNameForType(club: Club, nameType?: ClubNameType): string {
  switch (nameType) {
    case ClubNameType.ThreeLetterName:
      return club.threeLetterName ?? club.shortName ?? club.fullName;
    case ClubNameType.ShortName:
      return club.shortName ?? club.fullName;
    case ClubNameType.FullName:
      return club.fullName;
    case ClubNameType.NickName:
      return club.nickName ?? club.shortName ?? club.fullName;
    default:
      return club.shortName;
  }
}

/**
 * Keyed on `GoalTypeInterface` and Partial, so the compiler shows which members are unmapped and
 * the `?? 'goal'` fallback below stays live. A plain `Record<string, …>` made the fallback
 * statically dead and hid that two of the four members had no entry.
 *
 * `PenaltyShootout` is deliberately absent AND filtered out entirely — a shootout goal is not part
 * of the run of play, which is the same exclusion `buildTimeline` and `reports.service.ts` apply.
 * Mapping it here would have shown it beside the 90-minute score as an ordinary goal.
 */
const GOAL_TYPE_TO_EVENT_TYPE: Partial<Record<GoalTypeInterface, QuickViewEventType>> = {
  [GoalType.Normal]: 'goal',
  [GoalType.Penalty]: 'penalty',
  [GoalType.OwnGoal]: 'own-goal',
};

const isRunOfPlayGoal = (goalType: GoalTypeInterface | undefined): boolean => goalType !== GoalType.PenaltyShootout;

const oppositeSide = (side: 'home' | 'away'): 'home' | 'away' => (side === 'home' ? 'away' : 'home');

/**
 * One player's goals, split by how they were scored, and placed on the side each one counts for.
 *
 * An own goal is listed under the side that BENEFITED, with the scorer's name — the convention
 * every scoreboard uses, and the one the match detail Timeline already follows. `mapLineup`
 * attributes it to the scorer's own lineup row (that is the right place on a per-player lineup),
 * so this view flips it back across the halfway line.
 */
const goalGroupsForPlayer = (lineup: PlayerLineup, side: 'home' | 'away'): SidedEventGroup[] => {
  const byType = new Map<QuickViewEventType, GoalInfo[]>();

  for (const goal of (lineup.goals ?? []).filter((goal) => isRunOfPlayGoal(goal.goalType))) {
    // A goal imported before `goalType` existed has none, and reads as a normal goal — which is
    // exactly what it rendered before SC-718, so no historical match changes.
    const type = (goal.goalType && GOAL_TYPE_TO_EVENT_TYPE[goal.goalType]) ?? 'goal';
    byType.set(type, [...(byType.get(type) ?? []), { minute: goal.minute, phase: goal.phase }]);
  }

  return [...byType.entries()].map(([type, occurrences]) => ({
    side: type === 'own-goal' ? oppositeSide(side) : side,
    player: lineup.player.name,
    profileId: lineup.player.profileId,
    type,
    occurrences,
  }));
};

/**
 * A player's bookings. `yellowCards` is minutes-only in the order shown, so index 0 is the first
 * (or only) yellow and index 1 is the second that sent them off — the same convention the match
 * detail Lineups tab uses. A red at the same minute as a second yellow is that same dismissal,
 * not a separate one.
 */
const cardGroupsForPlayer = (lineup: PlayerLineup, side: 'home' | 'away'): SidedEventGroup[] => {
  const identity = { side, player: lineup.player.name, profileId: lineup.player.profileId };
  const yellows = lineup.yellowCards ?? [];

  const groups: SidedEventGroup[] = yellows.map((minute, index) => ({
    ...identity,
    type: index === 0 ? 'yellow' : 'second-yellow',
    occurrences: [{ minute }],
  }));

  const secondYellowMinute = yellows.length >= 2 ? yellows[1] : undefined;
  if (lineup.redCard !== undefined && lineup.redCard !== secondYellowMinute) {
    groups.push({ ...identity, type: 'red', occurrences: [{ minute: lineup.redCard }] });
  }

  return groups;
};

type SidedEventGroup = PlayerEventGroup & { side: 'home' | 'away' };

/**
 * Builds the per-side event lines the expanded quick view shows (SC-718).
 *
 * Before this it showed goals — all with one generic ball glyph — and red cards, so a penalty was
 * indistinguishable from a normal goal, an own goal appeared on neither side, and a booking of
 * either colour was invisible.
 */
function buildPlayerStats(fixture: Match) {
  const sides: { side: 'home' | 'away'; players: PlayerLineup[] }[] = [
    { side: 'home', players: [...(fixture.lineups?.home ?? []), ...(fixture.subs?.home ?? [])] },
    { side: 'away', players: [...(fixture.lineups?.away ?? []), ...(fixture.subs?.away ?? [])] },
  ];

  const groups = sides.flatMap(({ side, players }) =>
    players.flatMap((lineup) => [...goalGroupsForPlayer(lineup, side), ...cardGroupsForPlayer(lineup, side)])
  );

  const forSide = (side: 'home' | 'away'): PlayerEventGroup[] =>
    groups
      .filter((group) => group.side === side)
      .map(({ side: _side, ...group }) => ({
        ...group,
        occurrences: [...group.occurrences].sort((a, b) => a.minute - b.minute),
      }))
      .sort(sortEventGroups);

  return { homeEvents: forSide('home'), awayEvents: forSide('away') };
}

function formatKickoffTime(fixture: Match): string {
  if (fixture.status !== MatchStatus.NotKickedOff) return '';
  if (!fixture.time) return 'N/A';

  let time = fixture.time;
  const [hour, minutes] = fixture.time.split(':');
  if (!hour || !minutes) {
    console.warn(`Invalid time "${fixture.time}" returned for fixture id: ${fixture.id}`);
    return 'N/A';
  }
  // If the hour is returned as a single digit, e.g. 9, convert to ISO by prefixing with zero: 09
  if (hour.length === 1) {
    time = `0${hour}:${minutes}`;
  }
  return format(parseISO(`2024-01-01T${time}`), FIXTURE_MATCH_TIME);
}

// ── Sub-components ────────────────────────────────────────────

type ScoreBlockProps = {
  fixture: Match;
  formattedTime: string;
  isLiveMatch: boolean;
  matchCurrentMinute?: string;
  hasScores: boolean;
};

const ScoreBlock = ({ fixture, formattedTime, isLiveMatch, matchCurrentMinute, hasScores }: ScoreBlockProps) => (
  <>
    {hasScores && (
      <div
        className={cn('fixture-match__score', {
          'fixture-match__score--live': isLiveMatch,
        })}
      >
        {isHalfTimeBreak(fixture.status) && (
          <span className="fixture-match__pill fixture-match__pill--ht text-3xs">HT</span>
        )}
        {isLiveMatch && !isHalfTimeBreak(fixture.status) && (
          <span className="fixture-match__pill fixture-match__pill--live text-3xs">
            <span className="fixture-match__pill-dot" />
            {matchCurrentMinute && <span>{matchCurrentMinute}&apos;</span>}
          </span>
        )}
        <div className="fixture-match__score-line small-text">
          <span>{fixture.score.current.home}</span>
          <span> - </span>
          <span>{fixture.score.current.away}</span>
        </div>
        {fixture.penaltyScores && (
          <span className="fixture-match__score-pens text-3xs">
            {fixture.penaltyScores.home} P {fixture.penaltyScores.away}
          </span>
        )}
        {fixture.status === MatchStatus.FirstHalfOfExtraTime && (
          <span className="fixture-match__score__fh_et fixture-match__score__status text-3xs">ET</span>
        )}
        {fixture.status === MatchStatus.SecondHalfOfExtraTime && (
          <span className="fixture-match__score__sh_et fixture-match__score__status text-3xs">ET</span>
        )}
        {fixture.status === MatchStatus.FullTimeAfterExtraTime && (
          <span className="fixture-match__score-status text-3xs">AET</span>
        )}
        {fixture.status === MatchStatus.PenaltyShootOut && (
          <span className="fixture-match__score-status text-3xs">Pens</span>
        )}
      </div>
    )}
    {fixture.status === MatchStatus.Abandoned && (
      <div className="fixture-match__pill fixture-match__pill--cancel text-3xs">A-A</div>
    )}
    {fixture.status === MatchStatus.Postponed && (
      <div className="fixture-match__pill fixture-match__pill--pp text-3xs">P-P</div>
    )}
    {fixture.status === MatchStatus.NotKickedOff && (
      <div className="fixture-match__time small-text">{formattedTime}</div>
    )}
  </>
);

type PlayerStatsListProps = {
  events: PlayerEventGroup[];
  side: 'home' | 'away';
  sideName: string;
  hasExtraTime: boolean;
};

// Links a scorer/card name to their profile page when a master football_players
// id is present, plain text otherwise. Always stops propagation on click: the
// mobile expandable variant nests this inside a `role="button"` row expander, and
// tapping a name must not also toggle that expander (harmless no-op on desktop,
// where there's no parent click handler to stop).
const ScorerName = ({ name, profileId }: { name: string; profileId?: string }) => {
  if (!profileId) {
    return <span>{name}</span>;
  }
  return (
    <Link
      className="fixture-match__player-link"
      to={`/players/${profileId}`}
      onClick={(event) => event.stopPropagation()}
    >
      {name}
    </Link>
  );
};

/**
 * The glyph for one event line. Shares `resolveTimelineGlyph` with the match detail Timeline tab
 * so a penalty, own goal or second yellow cannot come out looking like one thing here and another
 * there — the divergence SC-718 was raised about.
 *
 * A plain yellow/red is a flat CSS block rather than an image, reusing the long-standing
 * `.match-overview__yellow-card` / `--red-card` rules this view already had for reds.
 */
const EventGlyph = ({ type, sideName }: { type: QuickViewEventType; sideName?: string }) => {
  const glyph = resolveTimelineGlyph(type);

  if (glyph.variant === 'card') {
    return <div className={`match-overview__${glyph.cardModifier}-card`} role="img" aria-label={glyph.label} />;
  }

  // An own goal is the one line whose meaning inverts if you cannot see which column it is in:
  // it carries the scorer's name but sits under the opposing team. Naming the benefitting side
  // in the alt text removes the contradiction for a screen reader.
  const alt = type === 'own-goal' && sideName ? `${glyph.iconAlt}, counts for ${sideName}` : glyph.iconAlt;
  return <Image className="ball-icon" src={glyph.iconSrc} alt={alt} width={12} height={12} />;
};

const PlayerStatsList = ({ events, side, sideName, hasExtraTime }: PlayerStatsListProps) => {
  // Design scorer line: glyph · name · minutes; the away side reverses so the
  // glyph sits at the outer edge (SCSS flex-direction: row-reverse).
  const scorerClass = cn('fixture-match__scorer', 'text-3xs', { 'fixture-match__scorer--away': side === 'away' });

  return (
    <>
      {events.map(({ player, profileId, type, occurrences }, index) => (
        // Index-suffixed: two different players can share a name (an unidentified pair both come
        // through as "N/A"), and name+type alone would collide for them.
        <div key={`${player}-${type}-${index}`} className={scorerClass}>
          <EventGlyph type={type} sideName={sideName} />
          <ScorerName name={player} profileId={profileId} />
          {/* No length guard: a group only exists because it has at least one occurrence. */}
          <span className="mins">
            {occurrences.map((at) => `${formatPhaseAwareMinute(at.minute, at.phase, hasExtraTime)}'`).join(', ')}
          </span>
        </div>
      ))}
    </>
  );
};

type PlayerStatsData = {
  homeEvents: PlayerEventGroup[];
  awayEvents: PlayerEventGroup[];
};

type ExpandedScorersProps = {
  playerStats: PlayerStatsData;
  homeName: string;
  awayName: string;
  hasExtraTime: boolean;
};

const hasPlayerStatEvents = (playerStats: PlayerStatsData): boolean =>
  playerStats.homeEvents.length > 0 || playerStats.awayEvents.length > 0;

const ExpandedScorers = ({ playerStats, homeName, awayName, hasExtraTime }: ExpandedScorersProps) => {
  // Nothing to reveal — render no panel (the expand chevron is also hidden).
  if (!hasPlayerStatEvents(playerStats)) {
    return null;
  }

  return (
    <div className="fixture-match__detailwrap">
      <div className="fixture-match__scorers">
        {/* Which team a line belongs to was conveyed only by grid column. role=group plus a
            name gives a screen reader the boundary, without changing the DOM the layout uses. */}
        <div
          className="fixture-match__scorers-side fixture-match__scorers-side--home"
          role="group"
          aria-label={`${homeName} goals and cards`}
        >
          <PlayerStatsList
            events={playerStats.homeEvents}
            side="home"
            sideName={homeName}
            hasExtraTime={hasExtraTime}
          />
        </div>
        <div className="fixture-match__scorers-mid" />
        <div
          className="fixture-match__scorers-side fixture-match__scorers-side--away"
          role="group"
          aria-label={`${awayName} goals and cards`}
        >
          <PlayerStatsList
            events={playerStats.awayEvents}
            side="away"
            sideName={awayName}
            hasExtraTime={hasExtraTime}
          />
        </div>
      </div>
    </div>
  );
};

type MetaFooterProps = {
  fixture: Match;
  showAttendance: boolean;
  // Venue + referee belong to the expanded detail per the design; the always-
  // visible (non-expandable) placement keeps its attendance-only behaviour.
  includeMatchMeta?: boolean;
};

const hasAttendance = (fixture: Match): boolean => !!fixture.attendance && fixture.attendance > 0;

const AttendanceLabel = ({ attendance }: { attendance: number }) => (
  <span className={ATTENDANCE_CLASS}>
    <span className="fixture-match__meta-icon" title="Attendance">
      <PeopleIcon />
    </span>
    <span>{attendance.toLocaleString()}</span>
  </span>
);

// Always rendered, even empty, so every row in the list keeps the same grid and the scores
// stay aligned down the page.
const AttendanceColumn = ({ fixture }: { fixture: Match }) => (
  <div className="fixture-match__attendance-col text-3xs">
    {hasAttendance(fixture) && <AttendanceLabel attendance={fixture.attendance} />}
  </div>
);

// Design footer: pin venue · whistle referee · people attendance.
const MetaFooter = ({ fixture, showAttendance, includeMatchMeta = false }: MetaFooterProps) => {
  const showAtt = showAttendance && hasAttendance(fixture);
  const venue = includeMatchMeta ? fixture.stadium : undefined;
  const referee = includeMatchMeta ? fixture.referee : undefined;

  if (!venue && !referee && !showAtt) {
    return null;
  }

  return (
    <div className="fixture-match__meta-foot text-3xs">
      {venue && (
        <span className="fixture-match__meta-venue">
          <span className="fixture-match__meta-icon">
            <PinIcon />
          </span>
          <span>{venue}</span>
        </span>
      )}
      {referee && (
        <span className="fixture-match__meta-ref">
          <span className="fixture-match__meta-icon" title="Referee">
            <WhistleIcon />
          </span>
          <span>{referee}</span>
        </span>
      )}
      {showAtt && <AttendanceLabel attendance={fixture.attendance} />}
    </div>
  );
};

type AwayTeamContentProps = {
  team: Match['awayTeam'];
  clubName: string | undefined;
};

const AwayTeamContent = ({ team, clubName }: AwayTeamContentProps) => (
  <>
    <Image
      src={team.logo ?? defaultTeamBadge}
      alt={team.fullName}
      width={26}
      height={26}
      className={TEAM_LOGO_CLASS}
      style={!team.logo ? { width: 26, height: 26 } : undefined}
      variant={ImageVariant.SquareLogoXs}
    />
    <span
      className="fixture-match__team-name"
      style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
    >
      {clubName}
    </span>
  </>
);

// ── Main component ────────────────────────────────────────────

export const Fixture = ({
  fixture,
  showAttendance = false,
  attendancePlacement = 'footer',
  nameType,
  className,
  isFixtureExpandable = false,
  isExpanded: isExpandedExternal,
}: FixtureProps) => {
  const isMobile = isMobileClient();
  const attendanceInColumn = showAttendance && attendancePlacement === 'column' && !isMobile;
  const [isExpanded, setIsExpanded] = useState(isExpandedExternal);

  useEffect(() => {
    if (isExpandedExternal !== undefined) {
      setIsExpanded(isExpandedExternal);
    }
  }, [isExpandedExternal]);

  const getClubName = (club: Club) => getClubNameForType(club, nameType);

  const toggleFixtureDetails = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const { isLiveMatch } = parseMatchStatus(fixture.status);
  const hasScores = !(
    [MatchStatus.NotKickedOff, MatchStatus.Postponed, MatchStatus.Abandoned] as MatchStatus[]
  ).includes(fixture.status);
  const hasExtraTime = matchHadExtraTime(fixture.status);

  const formattedTime = useMemo(() => formatKickoffTime(fixture), [fixture]);
  const playerStats = useMemo(() => buildPlayerStats(fixture), [fixture]);
  // Only expandable when there are events worth revealing (goals / red cards),
  // so the chevron is hidden for matches with nothing to show.
  const isExpandable = isFixtureExpandable && !isFutureMatch(fixture.status) && hasPlayerStatEvents(playerStats);
  const matchCurrentMinute = useMemo(() => {
    if (isLiveMatch) return formatMatchMinute(fixture.status, fixture.currentMinute);
  }, [isLiveMatch, fixture.status, fixture.currentMinute]);

  const scoreBlockProps: ScoreBlockProps = { fixture, formattedTime, isLiveMatch, matchCurrentMinute, hasScores };
  const layoutProps: FixtureLayoutProps = {
    fixture,
    className,
    isExpanded,
    toggleFixtureDetails,
    getClubName,
    scoreBlockProps,
    // The column replaces the footer figure rather than repeating it.
    showAttendance: showAttendance && !attendanceInColumn,
    attendanceInColumn,
    playerStats,
    hasExtraTime,
  };

  if (isMobile && isExpandable) {
    return <FixtureMobileExpandable {...layoutProps} />;
  }

  return <FixtureDesktop {...layoutProps} isExpandable={isExpandable} isMobile={isMobile} />;
};

// ── Layout variants ────────────────────────────────────────────

type FixtureLayoutProps = {
  fixture: Match;
  className?: string;
  isExpanded?: boolean;
  toggleFixtureDetails: () => void;
  getClubName: (club: Club) => string | undefined;
  scoreBlockProps: ScoreBlockProps;
  showAttendance: boolean;
  attendanceInColumn: boolean;
  playerStats: PlayerStatsData;
  hasExtraTime: boolean;
};

type FixtureDesktopProps = FixtureLayoutProps & {
  isExpandable: boolean;
  isMobile: boolean;
};

const FixtureMobileExpandable = ({
  fixture,
  className,
  isExpanded,
  toggleFixtureDetails,
  getClubName,
  scoreBlockProps,
  showAttendance,
  playerStats,
  hasExtraTime,
}: FixtureLayoutProps) => (
  <>
    <div
      className={cn('fixture-match', 'fixture-match--expandable', className, {
        'fixture-match--expanded': isExpanded,
      })}
      onClick={toggleFixtureDetails}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded ?? false}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleFixtureDetails();
        }
      }}
    >
      <div className="fixture-match__row-main">
        <div className={'fixture-match__home-team-wrapper'}>
          <div className={`${TEAM_LINK_CLASS} small-text`}>
            <span
              className="fixture-match__team-name"
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
            >
              {getClubName(fixture.homeTeam as Club)}
            </span>
            <Image
              src={fixture.homeTeam.logo ?? defaultTeamBadge}
              alt={fixture.homeTeam.fullName}
              width={26}
              height={26}
              className={TEAM_LOGO_CLASS}
              style={!fixture.homeTeam.logo ? { width: 26, height: 26 } : undefined}
              variant={ImageVariant.SquareLogoXs}
            />
          </div>
        </div>

        <div className={'fixture-match__score-link'}>
          <ScoreBlock {...scoreBlockProps} />
        </div>

        <div className={'fixture-match__away-team-wrapper'}>
          <div className={`${TEAM_LINK_CLASS} ${TEAM_LINK_CLASS}--away small-text`}>
            <AwayTeamContent team={fixture.awayTeam} clubName={getClubName(fixture.awayTeam as Club)} />
          </div>
        </div>
      </div>

      <div className="fixture-match__chev-wrap">
        <Image
          src={chevronRightIcon}
          alt={isExpanded ? 'collapse match details' : 'expand match details'}
          width={16}
          height={16}
          className={'fixture-match__chev'}
        />
      </div>

      {isExpanded && playerStats && (
        <ExpandedScorers
          playerStats={playerStats}
          homeName={fixture.homeTeam.fullName}
          awayName={fixture.awayTeam.fullName}
          hasExtraTime={hasExtraTime}
        />
      )}
      {isExpanded && <MetaFooter fixture={fixture} showAttendance={showAttendance} includeMatchMeta />}
    </div>
    {fixture.info && (
      <Link className={`${TEAM_LINK_CLASS} small-text`} to={`/match/${fixture.id}`}>
        <div className="fixture-match__info" dangerouslySetInnerHTML={{ __html: fixture.info }} />
      </Link>
    )}
    {isExpanded && (
      <LinkButton
        url={`/match/${fixture.id}`}
        variant={LinkVariant.Outlined}
        size={LinkSize.sm}
        label="Go to game"
        openInNewTab={false}
        style={{
          height: '4rem',
          display: 'block',
          textAlign: 'center',
          marginLeft: 'auto',
          marginRight: 'auto',
          marginTop: 'var(--spacing-3)',
        }}
      />
    )}
  </>
);

const FixtureDesktop = ({
  fixture,
  className,
  isExpanded,
  isExpandable,
  isMobile,
  toggleFixtureDetails,
  getClubName,
  scoreBlockProps,
  showAttendance,
  attendanceInColumn,
  playerStats,
  hasExtraTime,
}: FixtureDesktopProps) => (
  <>
    <div
      className={cn('fixture-match', className, {
        'fixture-match--expanded': isExpanded,
      })}
    >
      <div
        className={cn('fixture-match__row-main', {
          'fixture-match__row-main--attendance-column': attendanceInColumn,
        })}
      >
        {/* Mirrors the attendance column's width so the score stays centred on the row. */}
        {attendanceInColumn && <div className="fixture-match__row-spacer" aria-hidden="true" />}
        <div className={'fixture-match__home-team-wrapper'}>
          <Link className={`${TEAM_LINK_CLASS} small-text`} to={`/match/${fixture.id}`}>
            <span
              className="fixture-match__team-name"
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
            >
              {getClubName(fixture.homeTeam as Club)}
            </span>
            <Image
              src={fixture.homeTeam.logo ?? defaultTeamBadge}
              alt={fixture.homeTeam.fullName}
              width={26}
              height={26}
              className={TEAM_LOGO_CLASS}
              style={!fixture.homeTeam.logo ? { width: 26, height: 26 } : undefined}
              variant={ImageVariant.SquareLogoXs}
            />
          </Link>
        </div>

        <div className={'fixture-match__score-link-col'}>
          <Link className={'fixture-match__score-link'} to={`/match/${fixture.id}`}>
            <ScoreBlock {...scoreBlockProps} />
          </Link>
        </div>

        <div className={'fixture-match__away-team-wrapper'}>
          <Link className={`${TEAM_LINK_CLASS} ${TEAM_LINK_CLASS}--away small-text`} to={`/match/${fixture.id}`}>
            <AwayTeamContent team={fixture.awayTeam} clubName={getClubName(fixture.awayTeam as Club)} />
          </Link>
        </div>
        {attendanceInColumn && <AttendanceColumn fixture={fixture} />}
      </div>

      {isExpandable && !isMobile && (
        <button className={SHOW_MORE_BTN_CLASS} onClick={toggleFixtureDetails} aria-expanded={isExpanded ?? false}>
          <Image
            src={chevronRightIcon}
            alt={isExpanded ? 'collapse match details' : 'expand match details'}
            width={16}
            height={16}
            className={'fixture-match__show-more-icon'}
          />
        </button>
      )}

      {isExpanded && playerStats && (
        <ExpandedScorers
          playerStats={playerStats}
          homeName={fixture.homeTeam.fullName}
          awayName={fixture.awayTeam.fullName}
          hasExtraTime={hasExtraTime}
        />
      )}
      {isExpandable ? (
        isExpanded && <MetaFooter fixture={fixture} showAttendance={showAttendance} includeMatchMeta />
      ) : (
        <MetaFooter fixture={fixture} showAttendance={showAttendance} />
      )}
    </div>
    {fixture.info && (
      <Link className={`${TEAM_LINK_CLASS} small-text`} to={`/match/${fixture.id}`}>
        <div className="fixture-match__info" dangerouslySetInnerHTML={{ __html: fixture.info }} />
      </Link>
    )}
  </>
);
